
import React, { useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface UserManagementSectionProps {
  registerSectionRef: (id: string, element: HTMLElement | null) => void;
}

const UserManagementSection = ({ registerSectionRef }: UserManagementSectionProps) => {
  // Create refs for each section
  const userRolesRef = useRef<HTMLHeadingElement>(null);
  const profileSettingsRef = useRef<HTMLHeadingElement>(null);
  const subscriptionPlansRef = useRef<HTMLHeadingElement>(null);
  const sendgridSetupRef = useRef<HTMLHeadingElement>(null);
  
  // Register all section refs when component mounts or refs change
  useEffect(() => {
    registerSectionRef('user-roles', userRolesRef.current);
    registerSectionRef('profile-settings', profileSettingsRef.current);
    registerSectionRef('subscription-plans', subscriptionPlansRef.current);
    registerSectionRef('sendgrid-setup', sendgridSetupRef.current);
  }, [registerSectionRef]);

  return (
    <>
      <h2 
        id="user-roles" 
        ref={userRolesRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16"
      >
        User Roles and Permissions
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Understanding User Roles</CardTitle>
          <CardDescription>Different access levels in Discovery Hub</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub uses a role-based access control system to manage permissions. Each user is assigned a specific role that determines their access level and capabilities within the platform.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">System Roles</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Admin:</strong> Full system access, including:
                <ul className="list-disc pl-6 mt-1">
                  <li>User management across the entire platform</li>
                  <li>Company management</li>
                  <li>Subscription management</li>
                  <li>Access to admin-only features</li>
                </ul>
              </li>
              <li>
                <strong>User:</strong> Standard users with basic access:
                <ul className="list-disc pl-6 mt-1">
                  <li>Manage leads within their assigned companies</li>
                  <li>Edit their profile information</li>
                  <li>Access standard features based on their subscription</li>
                </ul>
              </li>
              <li>
                <strong>Sales:</strong> Specialized role for sales staff:
                <ul className="list-disc pl-6 mt-1">
                  <li>Enhanced lead management capabilities</li>
                  <li>Lead scoring and prioritization</li>
                  <li>Sales-specific analytics</li>
                </ul>
              </li>
              <li>
                <strong>Member:</strong> Basic role for team members:
                <ul className="list-disc pl-6 mt-1">
                  <li>Access to assigned company data</li>
                  <li>Limited lead management capabilities</li>
                  <li>Collaboration features</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Company-Specific Roles</h4>
            <p>Within each company, the relationship between users and companies is managed through the user_companies table, which determines what actions users can perform within specific companies.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Company Admin:</strong> Full control over company settings, members, and data</li>
              <li><strong>Company Member:</strong> Standard access to company resources and leads</li>
              <li><strong>Company Viewer:</strong> Read-only access to company information</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="profile-settings" 
        ref={profileSettingsRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Profile Settings
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Managing Your Profile</CardTitle>
          <CardDescription>Updating personal information and preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The Profile section allows you to manage your personal information and account settings through a user-friendly interface.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Available Profile Information</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Basic Information:</strong> Name, email, and contact details</li>
              <li><strong>Profile Picture:</strong> Upload and manage your avatar</li>
              <li><strong>Company Association:</strong> View your primary company</li>
              <li><strong>Role Information:</strong> Your current system role</li>
              <li><strong>Account Creation Date:</strong> When your account was established</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Updating Your Profile</h4>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Navigate to your profile by clicking your avatar in the top-right corner</li>
              <li>Update your information in the profile form</li>
              <li>Click "Save Changes" to apply your updates</li>
            </ol>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Profile Security</h4>
            <p>
              Your profile is securely stored and managed through our authentication system. Only you and system administrators can access your full profile information.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="subscription-plans" 
        ref={subscriptionPlansRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Subscription Plans
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Managing Subscriptions</CardTitle>
          <CardDescription>Understanding and managing your subscription plan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub offers different subscription tiers to meet varying business needs, from individual users to enterprise-level organizations.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Available Plans</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Free:</strong> Basic features to get started
                <ul className="list-disc pl-6 mt-1">
                  <li>Up to 5 leads</li>
                  <li>Basic lead details</li>
                  <li>Email template access</li>
                </ul>
              </li>
              <li>
                <strong>Starter (€30/month):</strong> For growing businesses
                <ul className="list-disc pl-6 mt-1">
                  <li>Up to 1,000 leads</li>
                  <li>Advanced lead details</li>
                  <li>Email and call templates</li>
                  <li>Lead status tracking</li>
                </ul>
              </li>
              <li>
                <strong>Pro (€60/month):</strong> Complete solution for established businesses
                <ul className="list-disc pl-6 mt-1">
                  <li>Up to 20,000 leads</li>
                  <li>Complete lead management</li>
                  <li>Advanced analytics</li>
                  <li>Priority support</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Managing Your Subscription</h4>
            <p>
              You can view and manage your current subscription plan, upgrade to a higher tier, or update your billing information through the Subscriptions page.
            </p>
            <p>
              Each subscription plan includes a specific lead limit that determines how many leads you can store in the system. Upgrading your plan will increase this limit.
            </p>
          </div>

          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">User Management Features</h4>
            <p>For administrators, the system provides comprehensive user management capabilities:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>User Invitation:</strong> Invite new users to join your organization</li>
              <li><strong>Role Assignment:</strong> Assign and modify user roles</li>
              <li><strong>Company Assignment:</strong> Associate users with specific companies</li>
              <li><strong>User Search:</strong> Find users by name, email, or role</li>
              <li><strong>User Deletion:</strong> Remove users from the system when needed</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="sendgrid-setup" 
        ref={sendgridSetupRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        SendGrid Email Setup
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Configure SendGrid for Email Sending</CardTitle>
          <CardDescription>
            Step-by-step guide to set up SendGrid integration and verify sender identities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Overview Section */}
          <div className="space-y-2">
            <h4 className="font-semibold">Overview</h4>
            <p>
              Discovery Hub integrates with SendGrid to enable reliable email sending capabilities. 
              You can configure your own SendGrid account to send emails directly from your verified 
              email addresses, giving you full control over your email deliverability and reputation.
            </p>
          </div>

          {/* Prerequisites Section */}
          <div className="space-y-2">
            <h4 className="font-semibold">Prerequisites</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>A SendGrid account (free tier available at <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">sendgrid.com</a>)</li>
              <li>Access to the email account you want to send from</li>
              <li>Your work email configured in your profile</li>
            </ul>
          </div>

          {/* Step 1: Get API Key */}
          <div className="space-y-2 border-l-4 border-primary pl-4">
            <h4 className="font-semibold text-primary">Step 1: Generate SendGrid API Key</h4>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                Log in to your SendGrid account at{' '}
                <a href="https://app.sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  app.sendgrid.com
                </a>
              </li>
              <li>Navigate to <strong>Settings → API Keys</strong> in the left sidebar</li>
              <li>Click <strong>"Create API Key"</strong></li>
              <li>Enter a name for your key (e.g., "Discovery Hub Integration")</li>
              <li>
                Select <strong>"Full Access"</strong> permissions (required for sender verification)
              </li>
              <li>Click <strong>"Create & View"</strong></li>
              <li>
                <strong className="text-destructive">Important:</strong> Copy the API key immediately - 
                you won't be able to see it again!
              </li>
            </ol>
            <div className="mt-2 p-3 bg-muted rounded-md">
              <p className="text-sm">
                <strong>🔐 Security Note:</strong> Your API key is stored encrypted in our database 
                and is never displayed in plain text after initial entry.
              </p>
            </div>
          </div>

          {/* Step 2: Configure in Discovery Hub */}
          <div className="space-y-2 border-l-4 border-primary pl-4">
            <h4 className="font-semibold text-primary">Step 2: Configure SendGrid in Discovery Hub</h4>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Go to your <strong>Profile → Email Sending</strong> tab</li>
              <li>In the <strong>"SendGrid Email Configuration"</strong> section:</li>
              <ul className="list-disc pl-6 mt-1 space-y-1">
                <li>Toggle <strong>"Use SendGrid for Sending Emails"</strong> to ON</li>
                <li>Paste your SendGrid API key in the <strong>"SendGrid API Key"</strong> field</li>
                <li>Enter your <strong>From Name</strong> (e.g., "John Doe")</li>
                <li>Enter your <strong>From Email</strong> (must match a verified sender identity)</li>
                <li>Optionally add a <strong>Reply-To Email</strong></li>
              </ul>
              <li>Click <strong>"Save Configuration"</strong></li>
            </ol>
          </div>

          {/* Step 3: Verify Sender Identity */}
          <div className="space-y-2 border-l-4 border-primary pl-4">
            <h4 className="font-semibold text-primary">Step 3: Verify Sender Email Identity</h4>
            <p className="text-sm text-muted-foreground">
              SendGrid requires you to verify ownership of any email address you want to send from.
            </p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                In the <strong>"Sender Email Addresses"</strong> section, click <strong>"Add Sender"</strong>
              </li>
              <li>Fill in the sender details:</li>
              <ul className="list-disc pl-6 mt-1 space-y-1">
                <li><strong>Nickname:</strong> A friendly name (e.g., "Work Email")</li>
                <li><strong>From Email:</strong> The email address you want to send from</li>
                <li><strong>From Name:</strong> Your name as it appears to recipients</li>
                <li><strong>Reply-To Email:</strong> (Optional) Different email for replies</li>
              </ul>
              <li>Click <strong>"Create"</strong></li>
              <li>
                <strong>Check your inbox</strong> for a verification email from SendGrid
              </li>
              <li>Click the verification link in the email</li>
              <li>
                Return to Discovery Hub and click <strong>"Check Status"</strong> to confirm verification
              </li>
              <li>
                Once verified, click <strong>"Set as Default"</strong> to use this sender for all emails
              </li>
            </ol>
            <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm">
                <strong>⚠️ Verification Required:</strong> You cannot send emails until at least 
                one sender identity is verified. Check your spam folder if you don't receive 
                the verification email within a few minutes.
              </p>
            </div>
          </div>

          {/* Step 4: Test Configuration */}
          <div className="space-y-2 border-l-4 border-green-500 pl-4">
            <h4 className="font-semibold text-green-700 dark:text-green-400">Step 4: Test Your Configuration</h4>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Return to the <strong>"SendGrid Email Configuration"</strong> section</li>
              <li>Click <strong>"Send Test Email"</strong></li>
              <li>Check your inbox for the test email</li>
              <li>If received successfully, your configuration is complete! ✅</li>
            </ol>
          </div>

          {/* Troubleshooting Section */}
          <div className="space-y-2 mt-6">
            <h4 className="font-semibold">Troubleshooting</h4>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-sm">❌ "Invalid API Key" error</p>
                <ul className="list-disc pl-6 text-sm text-muted-foreground">
                  <li>Verify you copied the entire API key (starts with "SG.")</li>
                  <li>Ensure the API key has "Full Access" permissions</li>
                  <li>Check that the API key hasn't been deleted in SendGrid</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-sm">📧 Verification email not received</p>
                <ul className="list-disc pl-6 text-sm text-muted-foreground">
                  <li>Check your spam/junk folder</li>
                  <li>Verify the email address is typed correctly</li>
                  <li>Click "Resend Email" to trigger another verification email</li>
                  <li>Wait 5-10 minutes before retrying</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-sm">🚫 "Sender not verified" error</p>
                <ul className="list-disc pl-6 text-sm text-muted-foreground">
                  <li>Ensure you clicked the verification link in the email</li>
                  <li>Click "Check Status" to refresh verification status</li>
                  <li>The verification process can take a few minutes</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-sm">💌 Test email failed to send</p>
                <ul className="list-disc pl-6 text-sm text-muted-foreground">
                  <li>Confirm at least one sender identity is verified and set as default</li>
                  <li>Check that the "From Email" matches your verified sender</li>
                  <li>Review SendGrid's email activity logs for delivery details</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div className="space-y-2 mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">📋 Best Practices</h4>
            <ul className="list-disc pl-6 space-y-1 text-sm text-blue-800 dark:text-blue-200">
              <li>Use a professional email address (e.g., john@company.com instead of personal@gmail.com)</li>
              <li>Verify multiple sender identities if you send from different email addresses</li>
              <li>Keep your API key secure - never share it or commit it to version control</li>
              <li>Monitor your SendGrid dashboard for delivery statistics and reputation</li>
              <li>Set up domain authentication in SendGrid for improved deliverability (advanced)</li>
            </ul>
          </div>

          {/* Additional Resources */}
          <div className="space-y-2 mt-6">
            <h4 className="font-semibold">Additional Resources</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <a href="https://docs.sendgrid.com/ui/account-and-settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  SendGrid API Keys Documentation
                </a>
              </li>
              <li>
                <a href="https://docs.sendgrid.com/ui/sending-email/sender-verification" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  SendGrid Sender Identity Verification Guide
                </a>
              </li>
              <li>
                <a href="https://docs.sendgrid.com/ui/analytics-and-reporting/email-activity-feed" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  SendGrid Email Activity Monitoring
                </a>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default UserManagementSection;
